import { Link } from 'react-router-dom';
import type { PlanErrorFields, PlanFeature, PlanName } from '../../types/api';
import { isPlanError } from './planErrors';
import { featureMinPlan, planFeatureMessage, planLimitMessage, upgradePath } from './planLabels';
import { usePlanName } from './usePlan';

interface UpsellBaseProps {
  /** Sits in a row with other controls rather than on a line of its own. */
  inline?: boolean;
  className?: string;
}

interface UpsellFeatureProps extends UpsellBaseProps {
  feature: PlanFeature;
  /** The plan that unlocks it; defaults to the cheapest plan with the feature. */
  requiredPlan?: PlanName | null;
  fields?: never;
}

interface UpsellLimitProps extends UpsellBaseProps {
  fields: PlanErrorFields;
  /** The section's own title, when the cap's generic name would read wrong. */
  subject?: string;
  feature?: never;
}

export type UpsellProps = UpsellFeatureProps | UpsellLimitProps;

/** The sentence and the link, the same in every mode. */
function UpsellLine({
  message,
  target,
  inline,
  className,
}: { message: string; target?: PlanName | null } & UpsellBaseProps) {
  const classes = ['upsell'];
  if (inline) classes.push('upsell--inline');
  if (className) classes.push(className);

  return (
    <p className={classes.join(' ')} role="note">
      <span className="upsell__message">{message}</span>{' '}
      <Link className="upsell__link" to={upgradePath(target)}>
        Upgrade
      </Link>
    </p>
  );
}

/**
 * The one upsell: a short sentence saying what the plan does not include, and
 * an "Upgrade" link. Inline where the action was, never a modal: the user
 * reads why, and the rest of the screen stays usable.
 *
 * Feature mode ("Publishing is on Solo and up.") for a `PLAN_REQUIRED` gate;
 * limit mode ("Brands are full (1 on Free · Studio allows 3).") for a
 * `PLAN_LIMIT_REACHED` cap. Fed either by `usePlan()` before the click or by
 * the 402 body after it, through `UpsellFromError`.
 */
export function Upsell(props: UpsellProps) {
  const planName = usePlanName();
  const isLimit = props.fields !== undefined;
  const message = isLimit
    ? planLimitMessage(props.fields, { plan: planName, subject: props.subject })
    : planFeatureMessage(props.feature, props.requiredPlan);
  const target = isLimit
    ? props.fields.next_plan
    : (props.requiredPlan ?? featureMinPlan(props.feature));

  return (
    <UpsellLine
      message={message}
      target={target}
      inline={props.inline}
      className={props.className}
    />
  );
}

/**
 * The Upsell for a caught error, or nothing: renders only when `error` is a
 * 402 `PLAN_REQUIRED` / `PLAN_LIMIT_REACHED` with its fields. Every other
 * error stays with the caller's own message.
 */
export function UpsellFromError({
  error,
  subject,
  inline,
  className,
}: { error: unknown; subject?: string } & UpsellBaseProps) {
  if (!isPlanError(error) || error.plan === null) return null;
  const fields = error.plan;

  if (error.isPlanRequired) {
    return fields.feature ? (
      <Upsell
        feature={fields.feature}
        requiredPlan={fields.required_plan}
        inline={inline}
        className={className}
      />
    ) : (
      <UpsellLine
        message="This is not on your plan."
        target={fields.required_plan}
        inline={inline}
        className={className}
      />
    );
  }
  return <Upsell fields={fields} subject={subject} inline={inline} className={className} />;
}
